import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileBarChart } from 'lucide-react';
import { listReportRuns, getReportRun, type ReportFrequency } from '../api/reports';
import { getApiClient } from '../api/factory';
import { useAuthStore } from '../store/auth';
import { useT, type DictKey } from '../i18n';
import { PageContainer, PageHeader } from '../components/Page';
import { EmptyState } from '../components/EmptyState';
import { SkeletonList } from '../components/Skeleton';
import { formatSecs, formatDeltaPct, deltaTone, formatCsat, formatOpCsat } from '../lib/reportFormat';

const FREQ_KEY: Record<ReportFrequency, DictKey> = {
  daily: 'reports.frequency.daily',
  weekly: 'reports.frequency.weekly',
  monthly: 'reports.frequency.monthly',
};

// Страница «Отчёты» (ANX-068) — архив сгенерированных дайджестов. Admin-only;
// не-админам не показывается (nav скрывает, backend 403'нёт).
export function ReportsPage() {
  const t = useT();
  const operator = useAuthStore((s) => s.session?.operator);
  const isAdmin = operator?.role === 'admin';
  const [selected, setSelected] = useState<string | null>(null);

  const runsQuery = useQuery({
    queryKey: ['report_runs'],
    queryFn: () => listReportRuns(getApiClient()),
    enabled: isAdmin,
  });

  if (!isAdmin) {
    return (
      <PageContainer width="narrow">
        <PageHeader title={t('reports.title')} />
        <p className="text-sm text-slate-500">{t('reports.adminOnly')}</p>
      </PageContainer>
    );
  }

  const runs = runsQuery.data ?? [];

  return (
    <PageContainer width="wide">
      <PageHeader title={t('reports.title')} description={t('reports.subtitle')} />

      {runsQuery.isLoading ? (
        <SkeletonList rows={4} ariaLabel={t('common.loading')} />
      ) : runs.length === 0 ? (
        <EmptyState icon={FileBarChart} title={t('reports.empty')} hint={t('reports.emptyHint')} />
      ) : (
        <div className="grid gap-4 md:grid-cols-[20rem_1fr]">
          <ul className="divide-y divide-slate-200 rounded-md border border-slate-200 bg-white shadow-card">
            {runs.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => setSelected(r.id)}
                  className={
                    'flex w-full flex-col items-start px-4 py-3 text-left hover:bg-slate-50 ' +
                    (selected === r.id ? 'bg-accent-50' : '')
                  }
                >
                  <span className="text-sm font-medium text-slate-900">
                    {r.periodStart === r.periodEnd
                      ? r.periodStart
                      : `${r.periodStart} — ${r.periodEnd}`}
                  </span>
                  <span className="mt-0.5 text-xs text-slate-500">
                    {t(FREQ_KEY[r.frequency])} ·{' '}
                    {r.emailed
                      ? t('reports.sentTo').replace('{n}', String(r.recipientsCount))
                      : t('reports.notEmailed')}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div>
            {selected ? (
              <ReportDetail id={selected} />
            ) : (
              <p className="px-4 py-6 text-sm text-slate-500">{t('reports.selectHint')}</p>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
}

function ReportDetail({ id }: { id: string }) {
  const t = useT();
  const q = useQuery({
    queryKey: ['report_run', id],
    queryFn: () => getReportRun(getApiClient(), id),
  });

  if (q.isLoading) return <SkeletonList rows={3} ariaLabel={t('common.loading')} />;
  if (q.isError || !q.data) {
    return <p className="px-4 py-6 text-sm text-red-600">{t('reports.loadError')}</p>;
  }
  const m = q.data.metrics;

  return (
    <div className="space-y-4 rounded-md border border-slate-200 bg-white p-5 shadow-card">
      <h3 className="text-base font-semibold text-slate-900">
        {m.period_start === m.period_end
          ? m.period_start
          : `${m.period_start} — ${m.period_end}`}
      </h3>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Metric label={t('reports.metric.totalChats')} value={String(m.total_chats)} delta={m.chats_delta_pct} />
        <Metric label={t('reports.metric.firstResponse')} value={formatSecs(m.first_response_p50_secs)} />
        <Metric label={t('reports.metric.avgResponse')} value={formatSecs(m.avg_response_secs)} />
        <Metric
          label={t('reports.metric.csat')}
          value={formatCsat(m.csat_score_pct, m.csat_positive, m.csat_negative)}
        />
      </div>

      <div>
        <span className="text-xs text-slate-500">{t('reports.metric.busiest')}: </span>
        <span className="text-sm text-slate-900">{m.busiest ? `${m.busiest.label} (${m.busiest.count})` : '—'}</span>
      </div>

      {m.operators.length > 0 && (
        <div>
          <h4 className="mb-1 text-sm font-medium text-slate-800">{t('reports.section.operators')}</h4>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {m.operators.map((op) => (
                <tr key={op.name}>
                  <td className="py-1.5 text-slate-900">{op.name}</td>
                  <td className="py-1.5 text-right text-slate-500">
                    {t('reports.chatsN').replace('{n}', String(op.chats))}
                    {formatOpCsat(op.csat_positive, op.csat_negative) &&
                      ` · ${formatOpCsat(op.csat_positive, op.csat_negative)}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {m.top_tags.length > 0 && (
        <div>
          <h4 className="mb-1 text-sm font-medium text-slate-800">{t('reports.section.topTags')}</h4>
          <ul className="flex flex-wrap gap-2">
            {m.top_tags.map((tag) => (
              <li
                key={tag.name}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-2.5 py-0.5 text-xs"
              >
                <span className="h-2 w-2 rounded-full" style={{ background: tag.color }} aria-hidden="true" />
                {tag.name}
                <span className="text-slate-400">{tag.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, delta }: { label: string; value: string; delta?: number | null }) {
  const tone = deltaTone(delta);
  const deltaStr = formatDeltaPct(delta);
  const toneClass =
    tone === 'up' ? 'text-emerald-600' : tone === 'down' ? 'text-red-600' : 'text-slate-400';
  return (
    <div className="rounded-md bg-slate-50 px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-slate-900">{value}</div>
      {deltaStr && <div className={'text-xs font-medium ' + toneClass}>{deltaStr}</div>}
    </div>
  );
}
